/**
 * The route table.
 *
 * Every path mounted below is also listed in `BUILT_PATHS`
 * (`components/nav-sections.ts`), which is what the sidebar consults before it
 * renders a link. The two lists move together: adding a route here without
 * adding it there leaves the area disabled in the nav, and the reverse would be
 * a dead link — so `isRouteBuilt` is the guard, not anyone's memory.
 */

import { Navigate, Route, Routes } from "react-router";

import { AppShell } from "@/components/app-shell";
import { AdminRoute, ProtectedRoute } from "@/components/protected-route";
import { AiClaudePage, AiCopilotPage, AiPage } from "@/features/ai";
import {
  BackupActionsPage,
  BackupCoveragePage,
  BackupJobsPage,
  BackupPage,
} from "@/features/backup";
import { CloudAlertsPage, CloudChangesPage, CloudPage } from "@/features/cloud";
import { DashboardPage } from "@/features/dashboard";
import { DefenderPage } from "@/features/defender";
import {
  DynamicsFormsPage,
  DynamicsInvestigatePage,
  DynamicsPage,
  DynamicsPinpointPage,
  DynamicsQueuesPage,
  DynamicsReportsPage,
  DynamicsRequestsPage,
} from "@/features/dynamics";
import { ExecutiveOverviewPage } from "@/features/executive";
import { ExperiencePage } from "@/features/experience";
import { LicensesPage, ReclaimableAccountsPage } from "@/features/licenses";
import { LoginPage } from "@/features/login";
import { M365Page } from "@/features/m365";
import { NetworkPage } from "@/features/network";
import { NotFoundPage } from "@/features/not-found";
import { SecurityPage } from "@/features/security";

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/overview" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        {/* Every section page renders inside the shell, which owns the brand
            header and the section nav. */}
        <Route element={<AppShell />}>
          <Route path="/overview" element={<ExecutiveOverviewPage />} />
          <Route path="/websites" element={<DashboardPage />} />
          <Route path="/network" element={<NetworkPage />} />
          <Route path="/cloud" element={<CloudPage />} />
          {/* Cloud L2 drill-downs — the full lists behind the board's charts. */}
          <Route path="/cloud/alerts" element={<CloudAlertsPage />} />
          <Route path="/cloud/changes" element={<CloudChangesPage />} />
          <Route path="/backup" element={<BackupPage />} />
          {/* Backup L2 drill-downs — the full lists behind the board's charts. */}
          <Route path="/backup/jobs" element={<BackupJobsPage />} />
          <Route path="/backup/actions" element={<BackupActionsPage />} />
          <Route path="/backup/coverage" element={<BackupCoveragePage />} />
          <Route path="/dynamics" element={<DynamicsPage />} />
          {/* Dynamics L2 drill-downs — the full lists behind the board's charts.
              `/dynamics/requests` is the API Management screen, and the rail
              labels it that way: the rows it draws come from `NO-APIM-NAX`, the
              gateway in front of F&O, not from F&O itself. The path is kept as
              it is because bookmarks and in-app cross-links point at it — the
              label and the registry description moved, the URL did not.
              `/dynamics/queues` is the same integration path's second half and
              keeps its path for the same reason; the rail lists it last, beside
              the gateway screen, because no namespace is configured for it. */}
          <Route path="/dynamics/forms" element={<DynamicsFormsPage />} />
          <Route path="/dynamics/requests" element={<DynamicsRequestsPage />} />
          <Route path="/dynamics/queues" element={<DynamicsQueuesPage />} />
          {/* What to fix first: the ranked findings board. Renders for anyone
              and then answers 403 off the investigator list, like the two
              per-person screens — it names no people, but it does rank named
              parts of the business. No reason gate: it carries no `reason` and
              writes no audit row. */}
          <Route path="/dynamics/pinpoint" element={<DynamicsPinpointPage />} />
          {/* The F&O Performance Console's per-user layer: reason-gated and
              audit-logged by the backend (D7), so it needs no AdminRoute. Both
              routes render for anyone and then answer 403 off the investigator
              list, which is why the sidebar disables rather than hides them —
              see `components/nav-access.ts`. */}
          <Route path="/dynamics/investigate" element={<DynamicsInvestigatePage />} />
          <Route path="/dynamics/reports" element={<DynamicsReportsPage />} />
          {/* Browser RUM across the five apps the managed extension reports on.
              Aggregates only — no reason gate, no `?mail=`; the per-user layer
              stays on /dynamics/investigate. */}
          <Route path="/experience" element={<ExperiencePage />} />
          <Route path="/m365" element={<M365Page />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/defender" element={<DefenderPage />} />
          {/* Three AI pages, one per upstream: the Azure estate via ARM and
              Azure Monitor, Copilot via Graph's org reports, Claude via the
              Anthropic Admin API. The four remaining AI routes (/ai/estate,
              /ai/capacity, /ai/mcp, /ai/spend) are declared in the feature's
              params but unbuilt, and `isAiRouteBuilt` keeps the board from
              linking to them — see TODO 5.26. */}
          <Route path="/ai/overview" element={<AiPage />} />
          <Route path="/ai/copilot" element={<AiCopilotPage />} />
          <Route path="/ai/claude" element={<AiClaudePage />} />
          {/* The Proofpoint board merged into /security — one board, one
              collector, one nav entry. Kept as a redirect because the old path
              is in bookmarks and in the deck's own footnotes. */}
          <Route path="/proofpoint" element={<Navigate to="/security" replace />} />
          <Route path="/licences" element={<LicensesPage />} />
          <Route element={<AdminRoute />}>
            <Route path="/licences/accounts" element={<ReclaimableAccountsPage />} />
          </Route>
        </Route>
        {/* The websites board used to live here. */}
        <Route path="/dashboard" element={<Navigate to="/websites" replace />} />
        {/* Scaffold landing page, retired — NOVA lands on /overview. */}
        <Route path="/home" element={<Navigate to="/overview" replace />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
